import { formatBytes } from '~~/utils/formatBytes';
import type { ImageConstraints } from '~~/utils/imageConstraints';

// Shared client-side image validator for the Visibility upload flows (Logo / Cover / Portfolio).
// Runs before any upload: checks accepted format, max file size, min dimensions / short side and
// (Portfolio) ratio, reading the image dimensions in the browser. Collects EVERY violation so the
// UI can list all problems at once, and exposes ready-to-render banner content matching the design
// (single-file banner for Logo/Cover, per-file batch banner for Portfolio). Reused by task 2's crop
// modal. See the "Image sizing & cropping + format constraints" spec §5.

export type ImageViolationCode = 'invalid_format' | 'too_large' | 'too_small' | 'short_side' | 'ratio' | 'decode_failed';

export interface ImageViolation {
    code: ImageViolationCode;
    params?: Record<string, string | number>;
}

export interface ImageValidationFailure {
    file: File;
    violations: ImageViolation[];
}

export interface BatchValidationResult {
    valid: File[];
    failures: ImageValidationFailure[];
}

interface ImageDimensions {
    width: number;
    height: number;
}

const hasAcceptedType = (file: File, constraints: ImageConstraints): boolean => {
    if (file.type) return constraints.acceptedMimeTypes.includes(file.type);
    // Some browsers/OSes leave `file.type` empty — fall back to the extension.
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return constraints.acceptedExtensions.includes(extension);
};

// `from-image` normalises EXIF orientation so the checks use the displayed orientation
// (portrait phone photos), matching `useImageRotation`. Returns null on an undecodable file.
const readDimensions = async (file: File): Promise<ImageDimensions | null> => {
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const dimensions = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        return dimensions;
    } catch {
        return null;
    }
};

// Pure — collects every violated constraint, no i18n. Order: format, size, then dimension-based rules.
const collectViolations = async (file: File, constraints: ImageConstraints): Promise<ImageViolation[]> => {
    const violations: ImageViolation[] = [];
    const typeOk = hasAcceptedType(file, constraints);

    if (!typeOk) violations.push({ code: 'invalid_format' });
    if (file.size > constraints.maxSizeBytes) {
        violations.push({ code: 'too_large', params: { size: formatBytes(file.size) ?? '', max: formatBytes(constraints.maxSizeBytes) ?? '' } });
    }

    const dimensions = await readDimensions(file);
    if (!dimensions) {
        // Only flag "unreadable" when the format itself looked valid — an unsupported format already
        // has the clearer `invalid_format` message and shouldn't double up.
        if (typeOk) violations.push({ code: 'decode_failed' });
        return violations;
    }

    const tooNarrow = constraints.minWidth != null && dimensions.width < constraints.minWidth;
    const tooShort = constraints.minHeight != null && dimensions.height < constraints.minHeight;
    if (tooNarrow || tooShort) {
        violations.push({
            code: 'too_small',
            params: { width: dimensions.width, height: dimensions.height, minWidth: constraints.minWidth ?? 0, minHeight: constraints.minHeight ?? 0 },
        });
    }
    if (constraints.minShortSide != null) {
        const shortSide = Math.min(dimensions.width, dimensions.height);
        if (shortSide < constraints.minShortSide) violations.push({ code: 'short_side', params: { size: shortSide, min: constraints.minShortSide } });
    }
    if (constraints.minRatio != null && constraints.maxRatio != null) {
        const ratio = dimensions.width / dimensions.height;
        if (ratio < constraints.minRatio || ratio > constraints.maxRatio) violations.push({ code: 'ratio' });
    }
    return violations;
};

export default function useImageValidation() {
    const { t } = useTranslation();

    // Accepted formats are identical for every image type ("JPG, PNG ou WebP") — a single static string.
    const formats = (): string => t('visibility.image_upload.formats');

    // Grey constraint line shown up front in the drop zone — formats · max size · min dimension.
    // Never includes the ratio (spec §5: enforced but not shown).
    const buildConstraintHint = (constraints: ImageConstraints): string => {
        const parts: string[] = [formats(), t('visibility.image_upload.hint.max_size', { size: formatBytes(constraints.maxSizeBytes) ?? '' })];
        if (constraints.minWidth != null && constraints.minHeight != null) {
            parts.push(t('visibility.image_upload.hint.min_dimension', { width: constraints.minWidth, height: constraints.minHeight }));
        } else if (constraints.minShortSide != null) {
            parts.push(t('visibility.image_upload.hint.min_short_side', { size: constraints.minShortSide }));
        }
        return parts.join(' · ');
    };

    // Full-sentence message for the single-file banner (Logo / Cover).
    const describeViolation = (violation: ImageViolation): string => {
        switch (violation.code) {
            case 'too_large':
                return t('visibility.image_upload.errors.too_large', violation.params ?? {});
            case 'too_small':
                return t('visibility.image_upload.errors.too_small', violation.params ?? {});
            case 'short_side':
                return t('visibility.image_upload.errors.short_side', violation.params ?? {});
            case 'invalid_format':
                return t('visibility.image_upload.errors.invalid_format', { formats: formats() });
            case 'ratio':
                return t('visibility.image_upload.errors.ratio');
            case 'decode_failed':
                return t('visibility.image_upload.errors.decode_failed');
        }
    };

    // Compact fragment for the batch banner line "« name » : …, …" (Portfolio).
    const describeViolationShort = (violation: ImageViolation): string => {
        switch (violation.code) {
            case 'too_large':
                return t('visibility.image_upload.short.too_large', violation.params ?? {});
            case 'short_side':
                return t('visibility.image_upload.short.short_side', violation.params ?? {});
            case 'too_small':
                return t('visibility.image_upload.short.too_small', violation.params ?? {});
            case 'invalid_format':
                return t('visibility.image_upload.short.invalid_format');
            case 'ratio':
                return t('visibility.image_upload.short.ratio');
            case 'decode_failed':
                return t('visibility.image_upload.short.decode_failed');
        }
    };

    const validateImage = (file: File, constraints: ImageConstraints): Promise<ImageViolation[]> => collectViolations(file, constraints);

    const validateImages = async (files: File[], constraints: ImageConstraints): Promise<BatchValidationResult> => {
        const results = await Promise.all(files.map(async (file) => ({ file, violations: await collectViolations(file, constraints) })));
        return {
            valid: results.filter((result) => result.violations.length === 0).map((result) => result.file),
            failures: results.filter((result) => result.violations.length > 0),
        };
    };

    // Ready-to-render banner content — single file (Logo / Cover): "« name » ne respecte pas les
    // contraintes" + one full-sentence line per violated constraint.
    const formatSingleFailure = (failure: ImageValidationFailure): { title: string; lines: string[] } => ({
        title: t('visibility.image_upload.errors.file_invalid', { name: failure.file.name }),
        lines: failure.violations.map(describeViolation),
    });

    // Ready-to-render banner content — batch (Portfolio): "Certaines images ne respectent pas les
    // contraintes" + one "« name » : problem, problem" line per failing file.
    const formatBatchFailures = (failures: ImageValidationFailure[]): { title: string; lines: string[] } => ({
        title: t('visibility.image_upload.errors.batch_invalid'),
        lines: failures.map((failure) => t('visibility.image_upload.errors.file_line', { name: failure.file.name, problems: failure.violations.map(describeViolationShort).join(', ') })),
    });

    return { validateImage, validateImages, buildConstraintHint, formatSingleFailure, formatBatchFailures };
}
